import { Fragment, useEffect, useMemo, useState } from 'react';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import Button from '../../../reusable/Button/Button';
import Input from '../../../reusable/Input/Input';
import FilterDropdown from '../../../reusable/FilterDropdown/FilterDropdown';
import Pagination from '../../../reusable/DataTable/Pagination';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { toast } from '../../../reusable/Toast/toastStore';
import {
  affiliateErrorMessage,
  useGetApprovedGiveawaysQuery,
  useGetGiveawayWinnersQuery,
  useSubmitGiveawayWinnersMutation,
} from '../../../store/api/affiliateApi';
import { formatBalance, requestStatusTone } from '../useAffiliateRequests';
import { t } from '../../../i18n/i18n';
import './WinnerSubmission.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const HISTORY_PAGE_SIZE = 10;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMATTER.format(d);
};

/**
 * One line for a giveaway's winners in the history: still under review while
 * any is Processing; otherwise what they were decided as; nothing submitted
 * yet says so.
 */
const winnersStatus = (w = {}) => {
  if (w.Processing) return 'Processing';
  if (w.Approved) return 'Approved';
  if (w.Rejected) return 'Rejected';
  return 'Awaiting winners';
};

/** The winners of one giveaway, fetched when its history row is opened. */
function WinnerRows({ giveawayId }) {
  const { data, isLoading, isError } = useGetGiveawayWinnersQuery(giveawayId);
  const winners = data?.results ?? [];

  if (isLoading) {
    return (
      <div className="winner-detail">
        <Skeleton w="100%" h={32} radius={6} />
      </div>
    );
  }
  if (isError || !winners.length) {
    return (
      <div className="winner-detail">
        <p className="winner-detail__empty">
          {isError ? t("Could not load this giveaway's winners.") : t('No winners submitted for this giveaway yet.')}
        </p>
      </div>
    );
  }

  return (
    <div className="winner-detail">
      <div className="winner-detail__row winner-detail__row--head">
        <span>#</span>
        <span>{t('Account Email')}</span>
        <span>{t('Registered')}</span>
        <span>{t('Account Number')}</span>
        <span>{t('Account Created')}</span>
        <span>{t('Note')}</span>
        <span>{t('Status')}</span>
      </div>
      {winners.map((w, i) => (
        <div className="winner-detail__row" key={w.id}>
          <span>{i + 1}</span>
          <span className="winner-detail__truncate" title={w.email}>{w.email}</span>
          {/* An unregistered winner can't be issued an account until they sign up. */}
          <span>{w.user_registered ? t('Yes') : t('Not yet')}</span>
          <span>{w.login_id || '—'}</span>
          <span>{formatDate(w.issued_at)}</span>
          <span className="winner-detail__truncate" title={w.issue_error || w.admin_comment || undefined}>
            {w.issue_error || w.admin_comment || '—'}
          </span>
          <span>
            <StatusChip tone={requestStatusTone(w.status)}>{t(w.status)}</StatusChip>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Affiliate — Winner Submission. Submit winners for an approved giveaway
 * request, then review past submissions in an expandable history table —
 * 1:18085. `/affiliate/giveaways/approved` lists the giveaways; winners go to
 * and come back from `/affiliate/giveaways/<id>/winners`.
 */
export default function WinnerSubmission() {
  const { data, isLoading, isError } = useGetApprovedGiveawaysQuery();
  const giveaways = useMemo(() => data?.results ?? [], [data]);
  const references = useMemo(() => giveaways.map((g) => g.id), [giveaways]);

  const [selectedReference, setSelectedReference] = useState('');
  const activeReference = references.includes(selectedReference)
    ? selectedReference
    : references[0] || '';
  const selectedGiveaway = giveaways.find((g) => g.id === activeReference) ?? null;

  // As many inputs as the giveaway still has open slots for.
  const remainingSlots = Number(selectedGiveaway?.winners?.remaining_slots) || 0;
  const canSubmitForGiveaway = Boolean(selectedGiveaway?.can_submit_winners) && remainingSlots > 0;
  const [winners, setWinners] = useState([]);
  useEffect(() => {
    setWinners(Array.from({ length: remainingSlots }, () => ''));
  }, [activeReference, remainingSlots]);

  const [submitWinners, { isLoading: isSubmitting }] = useSubmitGiveawayWinnersMutation();

  const summaryCards = [
    {
      icon: 'affiliate-revenue',
      label: t('Account Size'),
      value: formatBalance(selectedGiveaway?.challenge?.account_balance),
    },
    {
      icon: 'affiliate-signups',
      label: t('Slots Left'),
      value: selectedGiveaway
        ? t('{{left}} of {{total}}', {
            left: remainingSlots,
            total: selectedGiveaway.accounts_allotted ?? selectedGiveaway.quantity ?? 0,
          })
        : '—',
    },
    {
      icon: 'affiliate-calendar',
      label: t('Giveaway Ends'),
      value: formatDate(selectedGiveaway?.end_date),
    },
    {
      icon: 'affiliate-new-signup-revenue',
      label: t('Reference'),
      value: selectedGiveaway?.id || '—',
    },
  ];

  const updateWinner = (index, value) => {
    setWinners((prev) => prev.map((w, i) => (i === index ? value : w)));
  };

  // Partial submissions are fine — the API takes up to the open slots at once.
  const filled = winners.map((w) => w.trim()).filter(Boolean);
  const invalid = filled.filter((w) => !EMAIL.test(w));
  const duplicates = filled.filter((w, i) => filled.findIndex((x) => x.toLowerCase() === w.toLowerCase()) !== i);
  const formError = invalid.length
    ? invalid.length === 1
      ? t('Check this email: {{emails}}', { emails: invalid.join(', ') })
      : t('Check these emails: {{emails}}', { emails: invalid.join(', ') })
    : duplicates.length
      ? t('Each winner email must be unique.')
      : null;
  const canSubmit = canSubmitForGiveaway && filled.length > 0 && !formError && !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const res = await submitWinners({ id: activeReference, winners: filled }).unwrap();
      toast.success(
        t('Winners submitted'),
        res?.message ??
          (filled.length === 1
            ? t('{{count}} winner submitted for review.', { count: filled.length })
            : t('{{count}} winners submitted for review.', { count: filled.length }))
      );
      setWinners(Array.from({ length: Number(res?.remaining_slots) || 0 }, () => ''));
    } catch (err) {
      toast.error(t('Could not submit winners'), affiliateErrorMessage(err));
    }
  };

  /* ---- history: the same approved giveaways, newest first, ten a page ---- */
  const [expandedId, setExpandedId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const history = useMemo(
    () => [...giveaways].sort((a, b) => String(b.approved_at).localeCompare(String(a.approved_at))),
    [giveaways]
  );
  const pageCount = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const pageRows = history.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);
  const toggleExpanded = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const noTargetsLabel = isLoading ? t('Loading…') : isError ? t('Could not load giveaways') : t('No approved giveaways');

  return (
    <div className="affiliate-winner-submission">
      <form className="winner-form" onSubmit={handleSubmit}>
        <div className="winner-form__head">
          <span className="winner-form__title">
            <span className="icon winner-form__icon" style={icon('affiliate-winner-info')} />
            {t('Winner Information')}
          </span>
          <FilterDropdown
            options={references.length ? references : [noTargetsLabel]}
            value={activeReference || noTargetsLabel}
            onChange={setSelectedReference}
            label={activeReference || noTargetsLabel}
          />
        </div>

        <div className="winner-summary">
          {summaryCards.map((c) => (
            <div className="winner-summary-card" key={c.label}>
              <div className="winner-summary-card__head">
                <span className="icon winner-summary-card__icon" style={icon(c.icon)} />
                <span className="winner-summary-card__label">{c.label}</span>
              </div>
              <div className="winner-summary-card__value">
                {isLoading ? <Skeleton w="70px" h={20} /> : c.value}
              </div>
            </div>
          ))}
        </div>

        <div className="winner-entries">
          <div className="winner-entries__scroll">
            <div className="winner-entries__list">
              <div className="winner-entries__head">
                <span className="winner-entries__num">#</span>
                <span>{t('Account Email')}</span>
              </div>

              {isLoading ? (
                Array.from({ length: 3 }, (_, i) => (
                  <div className="winner-entries__row" key={`skeleton-${i}`}>
                    <span className="winner-entries__badge">{i + 1}</span>
                    <Skeleton w="100%" h={38} radius={8} />
                  </div>
                ))
              ) : canSubmitForGiveaway ? (
                winners.map((w, i) => (
                  <div className="winner-entries__row" key={i}>
                    <span className="winner-entries__badge">{i + 1}</span>
                    <Input
                      variant="form"
                      type="email"
                      placeholder="Enter email"
                      value={w}
                      onChange={(e) => updateWinner(i, e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                ))
              ) : (
                <p className="winner-detail__empty">
                  {selectedGiveaway
                    ? t('Every slot on this giveaway already has a winner.')
                    : t('No approved giveaways to submit winners for yet.')}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="winner-form__foot">
          <p className="winner-form__hint" role={formError ? 'alert' : undefined}>
            {formError ??
              t('Winners need an Alpha Capital account before theirs can be issued. Each email must be unique, and a previously rejected winner can be submitted again.')}
          </p>
          <div className="winner-form__submit">
            <span className="winner-form__progress">
              {filled.length}/{remainingSlots}
            </span>
            <Button
              variant="pill"
              height={28.8}
              paddingInline={16}
              htmlType="submit"
              disabled={!canSubmit}
              loading={isSubmitting}
            >
              {t('Submit')}
            </Button>
          </div>
        </div>
      </form>

      <section className="winner-history">
        <div className="winner-history__head">
          <span className="winner-history__title">
            <span className="icon winner-history__icon" style={icon('affiliate-recent-requests')} />
            {t('Winner Submission History')}
          </span>
          <span className="winner-history__subtitle">{t('Expand a giveaway to review submitted winners')}</span>
        </div>

        <div className="winner-history__scroll">
          <div className="winner-history__table">
            <div className="winner-history__row winner-history__row--head">
              <span>{t('Winners')}</span>
              <span>{t('Account Size')}</span>
              <span>{t('Approved On')}</span>
              <span>{t('Status')}</span>
            </div>

            {!isLoading && !history.length && (
              <p className="winner-detail__empty">{t('No approved giveaways yet.')}</p>
            )}

            {pageRows.map((g) => {
              const isOpen = expandedId === g.id;
              const submitted = Number(g.winners?.submitted) || 0;
              const status = winnersStatus(g.winners);
              return (
                <Fragment key={g.id}>
                  <button
                    type="button"
                    className={`winner-history__row is-expandable${isOpen ? ' is-open' : ''}`}
                    onClick={() => toggleExpanded(g.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="winner-history__quantity">
                      <span className="icon winner-history__chevron" style={icon('chevron-down')} />
                      <span className="icon winner-history__quantity-icon" style={icon('affiliate-signups')} />
                      {submitted === 1 ? t('{{count}} Winner', { count: submitted }) : t('{{count}} Winners', { count: submitted })}
                    </span>
                    <span className="winner-history__muted">
                      {formatBalance(g.challenge?.account_balance)}
                    </span>
                    <span className="winner-history__muted">{formatDate(g.approved_at)}</span>
                    <span>
                      <StatusChip tone={requestStatusTone(status)}>{t(status)}</StatusChip>
                    </span>
                  </button>

                  {isOpen && <WinnerRows giveawayId={g.id} />}
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="winner-history__foot">
          <Pagination
            page={historyPage}
            pageCount={pageCount}
            from={history.length ? (historyPage - 1) * HISTORY_PAGE_SIZE + 1 : 0}
            to={Math.min(historyPage * HISTORY_PAGE_SIZE, history.length)}
            total={history.length}
            onChange={setHistoryPage}
          />
        </div>
      </section>
    </div>
  );
}
